import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Fab,
  Zoom,
  IconButton,
  CircularProgress
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';

function LiveChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSuccess(false);
      setError('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const botToken = "7888596228:AAGiPd60oZWuZnMpd62R9Ybe1OG5pRMAMoA";
      const chatId = "-1002617880346";
      
      const message = `
📱 New Live Chat Request 📱
---------------------------
👤 Name: ${formData.name}
📧 Email: ${formData.email}
☎️ Phone: ${formData.phone}
💬 Message: ${formData.message || 'No message provided'}
---------------------------
`;
      
      const response = await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
          parse_mode: 'HTML'
        }),
      });
      
      const data = await response.json();
      
      if (data.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setError('Failed to send message: ' + (data.description || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <>
      {/* Chat toggle button */}
      <Fab 
        color="primary" 
        aria-label="chat"
        onClick={handleToggleChat}
        sx={{ 
          position: 'fixed', 
          right: 20, 
          bottom: 20, 
          zIndex: 2000,
          bgcolor: '#ff6f1a',
          '&:hover': {
            bgcolor: '#ff8c42'
          }
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>
      
      {/* Chat panel */}
      <Zoom in={isOpen}>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            right: 20,
            bottom: 90,
            width: 320,
            maxHeight: '70vh',
            overflow: 'auto',
            zIndex: 2000,
            bgcolor: '#232323',
            color: '#fff',
            p: 0
          }}
        >
          <Box sx={{ 
            bgcolor: '#ff6f1a', 
            p: 2, 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Live Support
            </Typography>
            <IconButton size="small" onClick={handleToggleChat} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ p: 3 }}>
            {success ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
                  Message Sent!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Thank you for reaching out. Our team will contact you shortly.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setSuccess(false)}
                  sx={{ 
                    borderColor: '#ff6f1a', 
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#ff8c42',
                      bgcolor: 'rgba(255, 111, 26, 0.1)'
                    }
                  }}
                >
                  Send Another Message
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Please fill out the form below and we'll get back to you shortly.
                </Typography>
                
                <TextField
                  fullWidth
                  label="Your Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ style: { color: '#aaa' } }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff6f1a' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6f1a' },
                    },
                    '& .MuiInputBase-input': {
                      color: '#fff'
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Your Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ style: { color: '#aaa' } }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff6f1a' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6f1a' },
                    },
                    '& .MuiInputBase-input': {
                      color: '#fff'
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Your Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ style: { color: '#aaa' } }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff6f1a' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6f1a' },
                    },
                    '& .MuiInputBase-input': {
                      color: '#fff'
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Your Message (Optional)"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ style: { color: '#aaa' } }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff6f1a' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6f1a' },
                    },
                    '& .MuiInputBase-input': {
                      color: '#fff'
                    }
                  }}
                />
                
                {error && (
                  <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>
                    {error}
                  </Typography>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    bgcolor: '#ff6f1a',
                    fontWeight: 700,
                    py: 1.5,
                    fontSize: 16,
                    '&:hover': {
                      bgcolor: '#ff8c42'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                </Button>
              </form>
            )}
          </Box>
        </Paper>
      </Zoom>
    </>
  );
}

export default LiveChat; 